<template>
    <div class="form-group text-form-group focus-form-group clearfix" :class="[colClass, customErrorClass]">
        <p class="pdf-inline" v-if="isViewOnly">
            <strong class="display-label">{{ effectiveLabel || effectiveLabelText }}:</strong>
            <span class="display-value" v-show="settings.effectivePrefixAddon">{{ settings.effectivePrefixAddon }}</span>
            <span class="display-value" v-show="effectiveMaskedValue">{{ effectiveMaskedValue }}</span>
            <span class="display-value" v-show="settings.suffixAddon">{{ settings.suffixAddon }}</span>
            <!-- <button class="btn btn-outline-secondary btn-sm" type="button" @click="isAdminEditing = true">Edit</button> -->
            <button class="passthrough icon icon-edit mr-1" type="button" v-if="showAdminEditControls" @click.prevent="startAdminEdit"><span class="sr-only">Edit</span></button>
        </p>
        <template v-else>
            <label class="control-label" :for="vmId" :aria-label="effectiveLabelText ?? undefined" :id="vmId + '_Label'" :class="{ dfRequired: settings.required, dfOptional: !settings.required, 'sr-only': effectiveLabel != effectiveLabelText }">
                {{ effectiveLabelText }}
                <HelpIcon :label="effectiveLabelText" :text="settings.helpText ?? undefined" />
                <button class="passthrough icon icon-close mr-1" type="button" v-if="isAdminEditing" @click.prevent="cancelAdminEdit" aria-label="Stop Edit" />
            </label>
            <!--
            <pre>HV: {{ JSON.stringify(hasValue) }}
TV: {{ JSON.stringify(tempValue) }}
V: {{ JSON.stringify(value) }}
EV: {{ JSON.stringify(effectiveValue) }}
DV: {{ JSON.stringify(defaultValue) }}</pre>
            -->
            <div :class="{ 'input-group': settings.effectivePrefixAddon || settings.suffixAddon }">
                <span class="input-group-prepend" v-if="settings.effectivePrefixAddon">
                    <span class="input-group-text">{{ settings.effectivePrefixAddon }}</span>
                </span>
                <template v-if="settings.layout == 1">
                    <!-- TODO constants -->
                    <textarea ref="inputElementRef" class="form-control" v-model="tempValue" @blur="markEdited" :placeholder="effectivePlaceholderText" :id="vmId" :aria-required="settings.required ? 'true' : 'false'" :mask="settings.effectiveInputMask" :mask-reverse="settings.effectiveReverseMask" :class="{ dfRequired: settings.required }" :rows="settings.rows" :maxlength="settings.maxLength || undefined" :aria-describedby="errorIds" />
                </template>
                <template v-else>
                    <template v-if="settings.useDatePicker">
                        <DatePicker
                            ref="inputElementRef"
                            v-model="tempValue"
                            :config="datepickerConfig"
                            :placeholder="settings.effectivePlaceholderText"
                            :ariaLabelledBy="vmId + '_Label'"
                            :ariaRequired="settings.required"
                            :ariaDescribedBy="errorIds"
                            :id="vmId"
                            @html-input="
                                handleInput($event);
                                markEdited();
                            "
                        />
                    </template>
                    <template v-else>
                        <input ref="inputElementRef" @blur="markEdited" @input="handleInput($event as InputEvent)" class="form-control" :value="tempValue" :type="settings.effectiveHtmlType || 'text'" :placeholder="effectivePlaceholderText" :id="vmId" :aria-required="settings.required ? 'true' : 'false'" :mask="settings.effectiveInputMask" :mask-reverse="settings.effectiveReverseMask" :class="{ dfRequired: settings.required }" :maxlength="settings.maxLength || undefined" :step="settings.effectiveHtmlStep || undefined" :min="settings.effectiveHtmlMin || undefined" :aria-describedby="errorIds" />
                    </template>
                </template>
                <span class="input-group-append" v-if="settings.suffixAddon">
                    <span class="input-group-text">{{ settings.suffixAddon }}</span>
                </span>
                <!--
                <button class="btn btn-outline-primary btn-sm mt-1" type="button" v-if="isAdminEditing" @click.prevent="saveAdminEdit">Save</button>
                -->
            </div>
        </template>
        <ErrorList :data="data" :id="errorId" />
        <Comments :data="data" />
        <AdminSettingsDifferences :data="data" />
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed, inject } from "vue";
import { createTextMaskInputElement } from "text-mask-core";
import { useObservable } from "@vueuse/rxjs";
import { map } from "rxjs/operators";
import TextboxDef from "../elements/TextboxDef";
import { TextboxSettings } from "@cs-ts/textbox-settings";
import DatePicker from "@/lib/DatePicker.3.vue";
import { props, Props, components, setup } from "./BaseDefMixin.3";
import { useMainStore } from "@/mainStore";
import FlatPickr from "vue-flatpickr-component";
export default defineComponent({
    components: {
        ...components,
        DatePicker,
    },
    props: props,
    //@ts-ignore
    setup(props: Props<TextboxDef, TextboxSettings>) {
        const mainStore = useMainStore();
        const base = setup<TextboxDef, TextboxSettings>(props);

        const mustMask = inject("mustMask", () => false);
        const hiddenElementsInjection = inject("hiddenElements", () => null as string[] | null);

        const textMaskInputElement = ref(null as null | ReturnType<typeof createTextMaskInputElement>);
        const inputElementRef = ref(null as null | HTMLInputElement | HTMLTextAreaElement | typeof FlatPickr);
        const tempValue = ref<string | null>(null);

        const defaultValue = useObservable(props.data.defaultValue$().pipe(map(i => (i.length ? i[0] : null))));
        const hasValue = useObservable(props.data.hasValue$());
        const effectiveLabel = useObservable(props.data.effectiveLabel$());
        const effectiveLabelText = useObservable(props.data.effectiveLabelText$());
        const maskedValue = useObservable(props.data.maskedValue$().pipe(map(i => (i.length ? i[0] : null))));

        const effectiveValue = computed(() => {
            return hasValue.value ? base.value.value : defaultValue.value;
        });
        const effectivePlaceholderText = computed(() => {
            return base.settings.value.effectivePlaceholderText;
        });
        const inputElement = computed(() => {
            return base.settings.value.useDatePicker ? (inputElementRef.value as typeof FlatPickr)?.flatPickr._input : inputElementRef.value;
        });
        const hiddenElements = computed(() => hiddenElementsInjection());
        const isMasked = computed(() => {
            const extraDataShimDef = props.data.children.find(i => i.definition.type == "ExtraDataShimDef");
            return hiddenElements.value != null && hiddenElements.value.length > 0 && extraDataShimDef != null ? !hiddenElements.value.includes(extraDataShimDef.id!) : null;
        });
        const effectiveMaskedValue = computed(() => ((isMasked.value ?? mustMask()) ? maskedValue : effectiveValue));

        base.focus = () => {
            const doFocus = () => {
                if (inputElement.value == null) {
                    setTimeout(doFocus, 100);
                } else {
                    inputElement.value.focus();
                }
            };
            doFocus();
        };

        const markEdited = () => {
            setValue(tempValue.value);
            props.data.markEdited();
        };
        const handleInput = (event: InputEvent) => {
            if (textMaskInputElement.value) {
                textMaskInputElement.value.update((event.target! as HTMLInputElement).value);
            }
            tempValue.value = (event.target! as HTMLInputElement).value;
        };
        const createTextMaskElement = () => {
            if ((base.settings.value.effectiveHtmlType == null || ["text", "tel", "url", "search", "password"].includes(base.settings.value.effectiveHtmlType)) && base.settings.value.effectiveInputMask != null && !/^\s*$/.test(base.settings.value.effectiveInputMask) && inputElement.value != null) {
                const config = {
                    inputElement: inputElement.value,
                    mask: maskArray.bind(this),
                    guide: false,
                    placeholderChar: "\u2000",
                };
                textMaskInputElement.value = createTextMaskInputElement(config);
            } else {
                textMaskInputElement.value = null;
            }
        };

        const datepickerConfig = {
            allowInput: true,
            altInput: true,
            dateFormat: "m-d-Y",
            altFormat: "m-d-Y",
            disableMobile: true,
            defaultDate: "",
            wrap: true,
            clickOpens: false,
            onOpen: () => {
                mainStore.toggleFocusTrapActiveModals(false);
            },
            onClose: () => {
                mainStore.toggleFocusTrapActiveModals(true);
            },
        };

        const maskArray = (value: string | null) => {
            let maskStr = base.settings.value.effectiveInputMask;
            if (maskStr == null || /^\s*$/.test(maskStr)) {
                return null;
            }
            value = value || "";
            const result = [];
            const optionalMask = [];
            // Extend the recursive mask to the length of value if needed
            const recurseIndex = maskStr.indexOf("#");
            if (recurseIndex >= 0) {
                const repeatedPart = maskStr.substring(recurseIndex);
                while (maskStr.length <= value.length && repeatedPart.length > 0) {
                    maskStr += repeatedPart;
                }
            }

            // Walk the mask backwards so can skip optional characters at the end
            let optional = false;
            for (let i = maskStr.length - 1; i >= 0; i--) {
                const maskChar = maskStr[i];
                let maskPattern;
                if (maskChar == "A") {
                    maskPattern = /[A-Za-z0-9]/;
                    optional = false;
                } else if (maskChar == "S") {
                    maskPattern = /[A-Za-z]/;
                    optional = false;
                } else if (maskChar == "0" || maskChar == "9" || maskChar == "#") {
                    maskPattern = /[0-9]/;
                    optional = maskChar != "0" && i >= value.length;
                } else {
                    maskPattern = maskChar;
                    // Include optional suffix only if there is extra characters
                    // This allows the extra typed character to be matched to the optional mask
                    // Without this, the extra typed character will be silently dropped if they aren't the constant
                    if (optional && i < value.length) {
                        result.unshift.apply(result, optionalMask);
                        maskPattern = maskChar;
                        optional = false;
                    }
                }
                if (optional) {
                    optionalMask.unshift(maskPattern);
                } else {
                    result.unshift(maskPattern);
                }
            }
            return base.settings.value.effectiveReverseMask ? result.reverse() : result;
        };

        const setValue = (newValue: string | null) => {
            if (newValue != effectiveValue.value) {
                //if (isNullOrWhitespace(newValue)) {
                //    base.value.value = null;
                //    tempValue.value = defaultValue.value;
                //} else {
                //TODO generic parameter for value type
                //@ts-ignore
                base.value.value = newValue;
                //}
            }
        };

        watch(inputElement, () => createTextMaskElement());
        watch(tempValue, (newValue, _) => {
            if (!(props.data.calculationContext.nonLiveTextboxes || props.data.forceNonLive)) {
                setValue(newValue);
            }
        });

        tempValue.value = effectiveValue.value as string | null;
        watch(
            () => effectiveValue.value,
            newValue => {
                tempValue.value = newValue as string | null;
            },
        );
        if (props.data.forceNonLive) {
            watch(defaultValue, () => props.data.markEdited());
        }

        return {
            inputElementRef,
            textMaskInputElement,
            inputElement,
            tempValue,
            effectiveLabel,
            effectiveLabelText,
            maskedValue,
            effectivePlaceholderText,
            effectiveValue,
            effectiveMaskedValue,
            datepickerConfig,

            markEdited,
            handleInput,

            ...base.expose,
        };
    },
});
</script>
